import React from "react";
import { Badge } from "../../ui/badge";
import Image from "../../Tags/Image";
import Text from "../../Tags/Text";

const SCORE_CONFIG = {
  max_score: 100,
  best: { min: 85, max: 100 },
  good: { min: 70, max: 84 },
  average: { min: 46, max: 69 },
  poor: { min: 0, max: 45 },
};

const getColorsForGraph = (score) => {
  if (score <= 100 && score >= 85) {
    return { text_color: "text-green-700", bg_color: "bg-green-700" };
  }
  if (score <= 84 && score >= 70) {
    return { text_color: "text-green-400", bg_color: "bg-green-400" };
  }
  if (score <= 69 && score >= 46) {
    return { text_color: "text-orange-400", bg_color: "bg-orange-400" };
  }
  if (score <= 45 && score >= 0) {
    return { text_color: "text-red-500", bg_color: "bg-red-500" };
  }
  return { text_color: "text-gray-500", bg_color: "bg-gray-500" };
};

const getColorsForScoreBadge = (score) => {
  if (score <= 5 && score >= 4) {
    return {
      text_color: "text-green-700 dark:text-[#75E0A7]",
      bg_color: "bg-green-100 dark:bg-[#053321]",
      border_color: "border-green-700 dark:border-[#085D3A]",
    };
  }
  if (score === 3) {
    return {
      text_color: "text-green-400 dark:text-[#75E0A7]",
      bg_color: "bg-green-100 dark:bg-[#053321]",
      border_color: "border-green-400 dark:border-[#085D3A]",
    };
  }
  if (score < 3 && score >= 0) {
    return {
      text_color: "text-red-500 dark:text-[#FDA29B]",
      bg_color: "bg-red-100 dark:bg-[#55160C]",
      border_color: "border-red-500 dark:border-[#912018]",
    };
  }
  return { text_color: "text-gray-600", bg_color: "bg-gray-100", border_color: "border-gray-600" };
};

export default function ScorecardsContent({ reportData, className = "" }) {
  if (!reportData) {
    return (
      <div className="flex justify-center items-center flex-col space-y-3 py-16">
        <Image src="/images/coming-soon.svg" />
        <Text size="md">Scorecards unavailable</Text>
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="text-gray-700 text-base font-bold">Total score</p>
      <div>
        <span className={`text-[36px] font-bold ${getColorsForGraph(reportData?.total_score)?.text_color}`}>
          {reportData?.total_score}
        </span>
        <span className="text-[24px]">/{SCORE_CONFIG.max_score}</span>
      </div>
      <div className="rounded-full w-full h-2 bg-gray-200 relative my-3">
        <span
          style={{ width: `${reportData?.total_score ?? 0}%` }}
          className={`absolute left-0 top-0 ${getColorsForGraph(reportData?.total_score)?.bg_color} h-full rounded-full`}
        ></span>
      </div>

      <div className="mt-8 space-y-3">
        {reportData?.positive_highlights?.map((item, index) => (
          <div key={`positive-${index}`} className="relative pl-6">
            <span className="h-2.5 w-2.5 bg-green-500 rotate-45 inline-block absolute left-0 top-1.5"></span>
            <p className="leading-relaxed text-[14px]">{item}</p>
          </div>
        ))}
        {reportData?.areas_for_improvement?.map((item, index) => (
          <div key={`improvement-${index}`} className="relative pl-6">
            <span className="h-2.5 w-2.5 bg-orange-400 rotate-45 inline-block absolute left-0 top-1.5"></span>
            <p className="leading-relaxed text-[14px]">{item}</p>
          </div>
        ))}
      </div>

      <div>
        {reportData?.scorecards?.map((scoreObj, idx) => {
          const badgeColors = getColorsForScoreBadge(scoreObj?.score);
          return (
            <div key={`${scoreObj?.question}-${idx}`} className="mt-6 border-t pt-6">
              <p className="text-gray-700 text-base font-bold mb-3">{scoreObj?.question}</p>
              {scoreObj?.type === "yes_no" && (
                <Badge
                  className={`text-base p-3 ${(scoreObj?.answer || "").toLowerCase() === "no" ? "bg-red-100 dark:bg-[#55160C] text-red-600 dark:text-[#FDA29B] border border-red-600 dark:border-[#912018]" : "bg-green-100 dark:bg-[#053321] text-green-600 dark:text-[#75E0A7] border border-green-600 dark:border-[#085D3A]"}`}
                >
                  {scoreObj?.answer}
                </Badge>
              )}
              {scoreObj?.type === "score_5" && (
                <Badge
                  className={`text-base h-8 p-3 ${badgeColors.bg_color} ${badgeColors.text_color} ${badgeColors.border_color}`}
                >
                  Score: {scoreObj?.score}/5
                </Badge>
              )}
              <div className="space-y-3 mt-4">
                {scoreObj?.positive_highlights?.map((item, index) => (
                  <div key={`score-positive-${idx}-${index}`} className="relative pl-6">
                    <span className="h-2.5 w-2.5 bg-green-500 rotate-45 inline-block absolute left-0 top-1.5"></span>
                    <p className="leading-relaxed text-[14px]">{item}</p>
                  </div>
                ))}
                {scoreObj?.areas_for_improvement?.map((item, index) => (
                  <div key={`score-improvement-${idx}-${index}`} className="relative pl-6">
                    <span className="h-2.5 w-2.5 bg-orange-400 rotate-45 inline-block absolute left-0 top-1.5"></span>
                    <p className="leading-relaxed text-[14px]">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
